import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Button } from '@/components/ui/Button'
import { Drawer } from '@/components/ui/Drawer'
import {
  PageError,
  PageLoading,
  PageNotFound,
} from '@/components/ui/PageAsyncState'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { BomSheet } from '@/components/boms'
import { useCan } from '@/features/auth/AuthContext'
import {
  bomService,
  BomsApiClientError,
  type Bom,
  type BomDetail,
  type BomWoodLine,
  type BomHardwareLine,
  type BomCompareResult,
} from '@/services/boms'
import { materialService, type Material } from '@/services/materials'
import { productService, type Product } from '@/services/products'
import { compareBomVersions } from './bomCompare'
import { exportBomToExcel } from './bomExcelExport'
import {
  parseClipboardData,
  mapPastedRowsToWoodLines,
  mapPastedRowsToHardwareLines,
  duplicateRow,
  bulkDeleteRows,
  validateWoodLine,
  validateHardwareLine,
} from './bomSpreadsheetUtils'
import {
  Pencil,
  CheckCircle2,
  History,
  Printer,
  Save,
  X,
  Plus,
  Trash2,
  AlertTriangle,
  ArrowLeft,
  GitCompare,
  Copy,
  FileSpreadsheet,
  Layers,
} from 'lucide-react'

export function BomDetailPage() {
  const { bomId } = useParams<{ bomId: string }>()
  const navigate = useNavigate()

  // Permissions
  const canWrite = useCan('bom.write')
  const canActivate = useCan('bom.activate')

  // Main data
  const [bom, setBom] = useState<BomDetail | null>(null)
  const [product, setProduct] = useState<Product | null>(null)
  const [materials, setMaterials] = useState<Material[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Status & notifications
  const [conflictError, setConflictError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // In-place editing state
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [draftEditNote, setDraftEditNote] = useState('')
  const [draftWoodLines, setDraftWoodLines] = useState<BomWoodLine[]>([])
  const [draftHardwareLines, setDraftHardwareLines] = useState<BomHardwareLine[]>([])

  // History drawer state
  const [showHistory, setShowHistory] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [familyVersions, setFamilyVersions] = useState<Bom[]>([])
  const [selectedHistoryBom, setSelectedHistoryBom] = useState<BomDetail | null>(null)
  const [selectedHistoryLoading, setSelectedHistoryLoading] = useState(false)
  const [showDiff, setShowDiff] = useState(false)

  // Clone modal state
  const [showCloneModal, setShowCloneModal] = useState(false)
  const [cloneName, setCloneName] = useState('')
  const [cloneProductId, setCloneProductId] = useState<string>('')
  const [cloneAssemblyType, setCloneAssemblyType] = useState<'assembled' | 'kd'>('assembled')
  const [allProducts, setAllProducts] = useState<Product[]>([])
  const [cloning, setCloning] = useState(false)
  const [cloneError, setCloneError] = useState<string | null>(null)

  // Compare drawer state
  const [showCompareDrawer, setShowCompareDrawer] = useState(false)
  const [availableBoms, setAvailableBoms] = useState<Bom[]>([])
  const [selectedCompareBomId, setSelectedCompareBomId] = useState<string>('')
  const [compareResult, setCompareResult] = useState<BomCompareResult | null>(null)
  const [compareLoading, setCompareLoading] = useState(false)
  const [compareError, setCompareError] = useState<string | null>(null)


  // Reload data
  const reload = useCallback(async () => {
    if (!bomId) return
    setLoading(true)
    setError(null)
    setConflictError(null)
    try {
      const [detail, mats] = await Promise.all([
        bomService.getById(bomId),
        materialService.list(),
      ])
      if (!detail) {
        setBom(null)
        setError('BOM không tồn tại')
        return
      }
      setBom(detail)
      setMaterials(mats)

      if (detail.product_id) {
        try {
          const prod = await productService.getById(detail.product_id)
          setProduct(prod)
        } catch {
          setProduct(null)
        }
      } else {
        setProduct(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải BOM')
    } finally {
      setLoading(false)
    }
  }, [bomId])

  useEffect(() => {
    void reload()
  }, [reload])

  // Start editing
  const handleStartEdit = () => {
    if (!bom) return
    setDraftWoodLines(bom.woodLines.map((l) => ({ ...l })))
    setDraftHardwareLines(bom.hardwareLines.map((l) => ({ ...l })))
    setDraftEditNote('')
    setConflictError(null)
    setSuccessMessage(null)
    setIsEditing(true)
  }

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false)
    setConflictError(null)
    if (bom) {
      setDraftWoodLines([])
      setDraftHardwareLines([])
      setDraftEditNote('')
    }
  }

  // Save active (creates new version, archives old)
  const handleSaveActive = async () => {
    if (!bom) return
    setConflictError(null)
    setError(null)

    // Validation
    for (let i = 0; i < draftWoodLines.length; i++) {
      const line = draftWoodLines[i]
      if (!line.part_name?.trim()) {
        setError(`Dòng gỗ #${i + 1}: Vui lòng nhập tên chi tiết`)
        return
      }
      if (!line.qty_per || line.qty_per <= 0) {
        setError(`Dòng gỗ #${i + 1}: Số lượng phải lớn hơn 0`)
        return
      }
    }

    for (let i = 0; i < draftHardwareLines.length; i++) {
      const line = draftHardwareLines[i]
      if (!line.name_raw?.trim()) {
        setError(`Dòng ngũ kim #${i + 1}: Vui lòng nhập tên vật tư`)
        return
      }
      if (!line.qty_per || line.qty_per <= 0) {
        setError(`Dòng ngũ kim #${i + 1}: Số lượng phải lớn hơn 0`)
        return
      }
    }

    setSaving(true)
    try {
      const updated = await bomService.saveActive(bom.id, {
        base_version_seq: bom.version_seq,
        edit_note: draftEditNote.trim() || null,
        wood_lines: draftWoodLines.map((w, idx) => ({
          id: w.id?.startsWith('draft-') ? undefined : w.id,
          sort_order: idx + 1,
          part_name: w.part_name.trim(),
          part_group: w.part_group?.trim() || null,
          material_id: w.material_id || null,
          wood_type_raw: w.wood_type_raw?.trim() || null,
          dims_finished_t: w.dims_finished_t != null ? Number(w.dims_finished_t) : null,
          dims_finished_w: w.dims_finished_w != null ? Number(w.dims_finished_w) : null,
          dims_finished_l: w.dims_finished_l != null ? Number(w.dims_finished_l) : null,
          tenon: w.tenon?.trim() || null,
          qty_per: Number(w.qty_per),
          veneer_spec: w.veneer_spec?.trim() || null,
          notes: w.notes?.trim() || null,
        })),
        hardware_lines: draftHardwareLines.map((h, idx) => ({
          id: h.id?.startsWith('draft-') ? undefined : h.id,
          sort_order: idx + 1,
          name_raw: h.name_raw.trim(),
          spec_raw: h.spec_raw?.trim() || null,
          material_id: h.material_id || null,
          qty_per: Number(h.qty_per),
          uom_hint: h.uom_hint?.trim() || null,
          color_hint: h.color_hint?.trim() || null,
          notes: h.notes?.trim() || null,
        })),
      })

      setBom(updated)
      setIsEditing(false)
      setSuccessMessage(
        `Đã lưu BOM thành công (phiên bản v${updated.version_seq}${
          updated.edit_count ? ` · Đã sửa lần ${updated.edit_count}` : ''
        })`,
      )
      // If id changed because new version row created, redirect smoothly
      if (updated.id !== bom.id) {
        navigate(`/purchasing/boms/${updated.id}`, { replace: true })
      }
    } catch (err) {
      if (
        err instanceof BomsApiClientError &&
        (err.status === 409 || err.code.includes('CONFLICT'))
      ) {
        setConflictError(
          'BOM đã có phiên bản mới trong hệ thống, vui lòng tải lại trang rồi sửa tiếp.',
        )
      } else {
        setError(err instanceof Error ? err.message : 'Lưu BOM thất bại')
      }
    } finally {
      setSaving(false)
    }
  }

  // Activate BOM (for draft)
  const handleActivate = async () => {
    if (!bom) return
    setSaving(true)
    setError(null)
    try {
      const updated = await bomService.activate(bom.id)
      setBom(updated)
      setSuccessMessage('Đã kích hoạt BOM thành công!')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kích hoạt BOM thất bại')
    } finally {
      setSaving(false)
    }
  }

  // Open history drawer
  const handleOpenHistory = async () => {
    if (!bom) return
    setShowHistory(true)
    setSelectedHistoryBom(null)
    setShowDiff(false)
    setHistoryLoading(true)
    try {
      const versions = await bomService.getFamilyVersions(bom.bom_family_id)
      setFamilyVersions(versions)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải lịch sử BOM')
    } finally {
      setHistoryLoading(false)
    }
  }

  // Select historical BOM
  const handleSelectHistoryVersion = async (verId: string) => {
    setSelectedHistoryLoading(true)
    try {
      const verDetail = await bomService.getById(verId)
      setSelectedHistoryBom(verDetail)
      setShowDiff(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải chi tiết phiên bản')
    } finally {
      setSelectedHistoryLoading(false)
    }
  }

  // Open clone modal
  const handleOpenCloneModal = async () => {
    if (!bom) return
    setCloneName(`${bom.name} (Bản sao)`)
    setCloneProductId(bom.product_id || '')
    setCloneAssemblyType(bom.assembly_type)
    setCloneError(null)
    setShowCloneModal(true)
    try {
      const res = await productService.list({ pageSize: 100 })
      setAllProducts(res.items)
    } catch {
      // ignore
    }
  }

  // Execute clone
  const handleExecuteClone = async () => {
    if (!bom) return
    setCloning(true)
    setCloneError(null)
    try {
      const cloned = await bomService.clone(bom.id, {
        name: cloneName.trim() || undefined,
        target_product_id: cloneProductId ? cloneProductId : null,
        target_assembly_type: cloneAssemblyType,
      })
      setShowCloneModal(false)
      navigate(`/purchasing/boms/${cloned.id}`)
    } catch (err) {
      setCloneError(err instanceof Error ? err.message : 'Nhân bản BOM thất bại')
    } finally {
      setCloning(false)
    }
  }

  // Open compare drawer
  const handleOpenCompareDrawer = async () => {
    setShowCompareDrawer(true)
    setCompareResult(null)
    setSelectedCompareBomId('')
    setCompareError(null)
    try {
      const list = await bomService.list()
      setAvailableBoms(list.filter((b) => b.id !== bom?.id))
    } catch {
      // ignore
    }
  }

  // Select target BOM for compare
  const handleSelectCompareBom = async (targetId: string) => {
    setSelectedCompareBomId(targetId)
    if (!bom || !targetId) {
      setCompareResult(null)
      return
    }
    setCompareLoading(true)
    setCompareError(null)
    try {
      const res = await bomService.compare(bom.id, targetId)
      setCompareResult(res)
    } catch (err) {
      setCompareError(err instanceof Error ? err.message : 'So sánh thất bại')
    } finally {
      setCompareLoading(false)
    }
  }

  // Export BOM to Excel
  const handleExportExcel = (targetBom: BomDetail | null) => {
    if (!targetBom) return
    exportBomToExcel({
      itemCode: product?.code || targetBom.code_hint || targetBom.name,
      productName: product?.name || targetBom.name,
      productDimensions: product
        ? `${product.w ?? '—'} × ${product.d ?? '—'} × ${product.h ?? '—'} mm`
        : undefined,
      variantColor: product?.name ? undefined : 'Mặc định',
      assemblyType: targetBom.assembly_type,
      mainWoodType: targetBom.woodLines[0]?.wood_type_raw || 'Theo chi tiết',
      veneerSpec:
        targetBom.woodLines.find((w) => w.veneer_spec)?.veneer_spec || '—',
      bomCode: targetBom.code_hint || targetBom.name,
      versionSeq: targetBom.version_seq,
      editCount: targetBom.edit_count ?? 0,
      status: targetBom.status,
      preparedBy: targetBom.prepared_by,
      checkedBy: targetBom.checked_by,
      checkedBy2: targetBom.checked_by_2,
      approvedBy: targetBom.approved_by,
      signedOn: targetBom.signed_on,
      notes: targetBom.notes,
      woodLines: targetBom.woodLines,
      hardwareLines: targetBom.hardwareLines,
    })
  }

  // State for printing an older version
  const [printOldBom, setPrintOldBom] = useState<BomDetail | null>(null)

  const handlePrintOldVersion = (oldBom: BomDetail) => {
    setPrintOldBom(oldBom)
    setTimeout(() => {
      window.print()
      setTimeout(() => setPrintOldBom(null), 1000)
    }, 100)
  }

  // Add wood line
  const handleAddWoodLine = () => {
    const newLine: BomWoodLine = {
      id: `draft-${Date.now()}`,
      bom_id: bom?.id ?? '',
      sort_order: draftWoodLines.length + 1,
      part_name: '',
      part_group: null,
      material_id: null,
      dims_finished_t: null,
      dims_finished_w: null,
      dims_finished_l: null,
      dims_rough_t: null,
      dims_rough_w: null,
      dims_rough_l: null,
      tenon: null,
      qty_per: 1,
      veneer_spec: null,
      notes: null,
      wood_type_raw: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
    setDraftWoodLines([...draftWoodLines, newLine])
  }

  // Remove wood line
  const handleRemoveWoodLine = (index: number) => {
    setDraftWoodLines(draftWoodLines.filter((_, i) => i !== index))
  }

  // Update wood line field
  const handleUpdateWoodLine = (index: number, field: keyof BomWoodLine, val: unknown) => {
    const next = [...draftWoodLines]
    next[index] = { ...next[index], [field]: val }
    setDraftWoodLines(next)
  }

  // Add hardware line
  const handleAddHardwareLine = () => {
    const newLine: BomHardwareLine = {
      id: `draft-${Date.now()}`,
      bom_id: bom?.id ?? '',
      sort_order: draftHardwareLines.length + 1,
      name_raw: '',
      spec_raw: null,
      material_id: null,
      qty_per: 1,
      uom_hint: 'Cái',
      color_hint: null,
      notes: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
    setDraftHardwareLines([...draftHardwareLines, newLine])
  }

  // Remove hardware line
  const handleRemoveHardwareLine = (index: number) => {
    setDraftHardwareLines(draftHardwareLines.filter((_, i) => i !== index))
  }

  // Update hardware line field
  const handleUpdateHardwareLine = (index: number, field: keyof BomHardwareLine, val: unknown) => {
    const next = [...draftHardwareLines]
    next[index] = { ...next[index], [field]: val }
    setDraftHardwareLines(next)
  }

  // --- D10c: Spreadsheet-style editing states & handlers ---
  const [selectedWoodIndices, setSelectedWoodIndices] = useState<Set<number>>(new Set())
  const [selectedHardwareIndices, setSelectedHardwareIndices] = useState<Set<number>>(new Set())

  // Paste modal state
  const [pasteModalOpen, setPasteModalOpen] = useState(false)
  const [pasteTargetTable, setPasteTargetTable] = useState<'wood' | 'hardware'>('wood')
  const [pasteMode, setPasteMode] = useState<'append' | 'overwrite'>('append')
  const [pasteTargetRowIdx, setPasteTargetRowIdx] = useState<number>(0)
  const [pasteRawText, setPasteRawText] = useState('')
  const [parsedPasteRows, setParsedPasteRows] = useState<string[][]>([])

  // Row duplication (Ctrl+D)
  const handleDuplicateWoodLine = (idx: number) => {
    setDraftWoodLines((prev) => duplicateRow(prev, idx))
  }
  const handleDuplicateHardwareLine = (idx: number) => {
    setDraftHardwareLines((prev) => duplicateRow(prev, idx))
  }

  // Bulk selection toggles
  const handleToggleSelectAllWood = () => {
    if (selectedWoodIndices.size === draftWoodLines.length) {
      setSelectedWoodIndices(new Set())
    } else {
      setSelectedWoodIndices(new Set(draftWoodLines.map((_, i) => i)))
    }
  }

  const handleToggleSelectWoodLine = (idx: number) => {
    setSelectedWoodIndices((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  const handleToggleSelectAllHardware = () => {
    if (selectedHardwareIndices.size === draftHardwareLines.length) {
      setSelectedHardwareIndices(new Set())
    } else {
      setSelectedHardwareIndices(new Set(draftHardwareLines.map((_, i) => i)))
    }
  }

  const handleToggleSelectHardwareLine = (idx: number) => {
    setSelectedHardwareIndices((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  // Bulk deletion
  const handleBulkDeleteWood = () => {
    if (selectedWoodIndices.size === 0) return
    setDraftWoodLines((prev) => bulkDeleteRows(prev, selectedWoodIndices))
    setSelectedWoodIndices(new Set())
  }

  const handleBulkDeleteHardware = () => {
    if (selectedHardwareIndices.size === 0) return
    setDraftHardwareLines((prev) => bulkDeleteRows(prev, selectedHardwareIndices))
    setSelectedHardwareIndices(new Set())
  }

  // Keyboard navigation
  const handleCellKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    tableType: 'wood' | 'hardware',
    rowIdx: number,
    colIdx: number,
    totalRows: number,
  ) => {
    // Ctrl+D: duplicate current row
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
      e.preventDefault()
      if (tableType === 'wood') {
        handleDuplicateWoodLine(rowIdx)
      } else {
        handleDuplicateHardwareLine(rowIdx)
      }
      return
    }

    // Enter: move focus down to next row in same column
    if (e.key === 'Enter') {
      e.preventDefault()
      const nextRow = rowIdx + 1
      if (nextRow < totalRows) {
        const nextInput = document.querySelector<HTMLInputElement>(
          `input[data-table="${tableType}"][data-row="${nextRow}"][data-col="${colIdx}"]`
        )
        nextInput?.focus()
        nextInput?.select?.()
      } else {
        // At the last row, add new row and focus
        if (tableType === 'wood') {
          handleAddWoodLine()
        } else {
          handleAddHardwareLine()
        }
        setTimeout(() => {
          const nextInput = document.querySelector<HTMLInputElement>(
            `input[data-table="${tableType}"][data-row="${nextRow}"][data-col="${colIdx}"]`
          )
          nextInput?.focus()
          nextInput?.select?.()
        }, 60)
      }
      return
    }

    // Arrow keys
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      const nextRow = rowIdx + 1
      if (nextRow < totalRows) {
        const nextInput = document.querySelector<HTMLInputElement>(
          `input[data-table="${tableType}"][data-row="${nextRow}"][data-col="${colIdx}"]`
        )
        nextInput?.focus()
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const prevRow = rowIdx - 1
      if (prevRow >= 0) {
        const prevInput = document.querySelector<HTMLInputElement>(
          `input[data-table="${tableType}"][data-row="${prevRow}"][data-col="${colIdx}"]`
        )
        prevInput?.focus()
      }
    }
  }

  // Intercept paste on table cell
  const handleCellPaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    tableType: 'wood' | 'hardware',
    rowIdx: number,
  ) => {
    const text = e.clipboardData.getData('text')
    if (text.includes('\t') || text.includes('\n')) {
      e.preventDefault()
      setPasteTargetTable(tableType)
      setPasteTargetRowIdx(rowIdx)
      setPasteMode('overwrite')
      setPasteRawText(text)
      setParsedPasteRows(parseClipboardData(text))
      setPasteModalOpen(true)
    }
  }

  // Open paste dialog manually
  const handleOpenPasteModal = (tableType: 'wood' | 'hardware', rowIdx = 0) => {
    setPasteTargetTable(tableType)
    setPasteTargetRowIdx(rowIdx)
    setPasteMode('append')
    setPasteRawText('')
    setParsedPasteRows([])
    setPasteModalOpen(true)
  }

  const handlePasteChange = (text: string) => {
    setPasteRawText(text)
    setParsedPasteRows(parseClipboardData(text))
  }

  const handleConfirmPaste = () => {
    if (parsedPasteRows.length === 0) return

    if (pasteTargetTable === 'wood') {
      const newLines = mapPastedRowsToWoodLines(parsedPasteRows) as BomWoodLine[]
      if (pasteMode === 'append') {
        setDraftWoodLines((prev) => [...prev, ...newLines])
      } else {
        setDraftWoodLines((prev) => {
          const updated = [...prev]
          for (let i = 0; i < newLines.length; i++) {
            const targetIdx = pasteTargetRowIdx + i
            if (targetIdx < updated.length) {
              updated[targetIdx] = { ...updated[targetIdx], ...newLines[i] }
            } else {
              updated.push(newLines[i])
            }
          }
          return updated
        })
      }
    } else {
      const newLines = mapPastedRowsToHardwareLines(parsedPasteRows) as BomHardwareLine[]
      if (pasteMode === 'append') {
        setDraftHardwareLines((prev) => [...prev, ...newLines])
      } else {
        setDraftHardwareLines((prev) => {
          const updated = [...prev]
          for (let i = 0; i < newLines.length; i++) {
            const targetIdx = pasteTargetRowIdx + i
            if (targetIdx < updated.length) {
              updated[targetIdx] = { ...updated[targetIdx], ...newLines[i] }
            } else {
              updated.push(newLines[i])
            }
          }
          return updated
        })
      }
    }

    setPasteModalOpen(false)
    setPasteRawText('')
    setParsedPasteRows([])
  }

  // Diff comparison
  const diffResult = useMemo(() => {
    if (!bom || !selectedHistoryBom) return null
    return compareBomVersions(bom, selectedHistoryBom)
  }, [bom, selectedHistoryBom])

  if (loading) {
    return <PageLoading label="Đang tải dữ liệu định mức BOM..." />
  }

  if (error && !bom) {
    return (
      <div className="space-y-4 p-6">
        <PageError message={error} onRetry={() => void reload()} />
        <Button variant="secondary" onClick={() => navigate('/purchasing/boms')}>
          Về danh sách BOM
        </Button>
      </div>
    )
  }

  if (!bom) {
    return (
      <PageNotFound
        title="Không tìm thấy BOM"
        description="Bản ghi định mức không tồn tại hoặc đã bị xóa."
        action={
          <Button variant="secondary" onClick={() => navigate('/purchasing/boms')}>
            Về danh sách BOM
          </Button>
        }
      />
    )
  }

  const isShared = bom.mappedVariantIds && bom.mappedVariantIds.length > 1
  const itemCode = product?.code || bom.code_hint || bom.name
  const productName = product?.name || bom.name
  const productDimensions = product
    ? `${product.w ?? '—'} × ${product.d ?? '—'} × ${product.h ?? '—'} mm`
    : '—'

  return (
    <div className="space-y-4 pb-12">
      {/* 1. Breadcrumbs */}
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mua hàng', to: '/purchasing' },
          { label: 'Định mức (BOM)', to: '/purchasing/boms' },
          { label: itemCode },
        ]}
      />

      {/* 2. Top alerts */}
      {conflictError && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 flex items-start gap-3 shadow-sm">
          <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold">Xung đột phiên bản BOM (409 Conflict)</h4>
            <p className="mt-1">{conflictError}</p>
            <Button
              variant="secondary"
              className="mt-2 text-xs"
              onClick={() => {
                void reload()
              }}
            >
              Tải lại trang ngay
            </Button>
          </div>
        </div>
      )}

      {error && !conflictError && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex items-start gap-3 shadow-sm">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p>{error}</p>
          </div>
          <button
            type="button"
            className="text-amber-600 hover:text-amber-800"
            onClick={() => setError(null)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-start gap-3 shadow-sm">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">{successMessage}</p>
          </div>
          <button
            type="button"
            className="text-emerald-600 hover:text-emerald-800"
            onClick={() => setSuccessMessage(null)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* 3. Cảnh báo dùng chung */}
      {isShared && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3.5 text-sm text-amber-900 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold">Cảnh báo dùng chung:</span> BOM này đang
            được dùng cho <strong>{bom.mappedVariantIds.length}</strong> điều kiện (màu ×
            chế độ lắp ráp). Việc lưu chỉnh sửa sẽ tự động cập nhật cho tất cả các điều
            kiện này.
          </div>
        </div>
      )}

      {/* Print CSS Styles for A4 Landscape and clean printout */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 8mm 10mm 12mm 10mm;
          }
          aside, header, nav, .print\\:hidden, button, .no-print, [role="presentation"] {
            display: none !important;
          }
          body, html {
            background: #fff !important;
            color: #000 !important;
            margin: 0 !important;
            padding: 0 !important;
            font-family: var(--font-sans), 'Inter', sans-serif !important;
          }
          main, div {
            box-shadow: none !important;
          }
          section {
            break-inside: avoid;
          }
        }
      `}</style>

      {/* 4. Top Action Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm print:hidden">
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => navigate('/purchasing/boms')}
            className="flex items-center gap-1.5"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Về danh sách</span>
          </Button>
          <span className="text-xs text-slate-400">|</span>
          <Button
            variant="secondary"
            onClick={handleOpenHistory}
            className="flex items-center gap-1.5 text-indigo-700 bg-indigo-50 border-indigo-200 hover:bg-indigo-100"
          >
            <History className="h-4 w-4" />
            <span>BOM cũ (Lịch sử)</span>
          </Button>
          <Button
            variant="secondary"
            onClick={handleOpenCompareDrawer}
            className="flex items-center gap-1.5 text-slate-700 bg-white border-slate-200 hover:bg-slate-50"
          >
            <GitCompare className="h-4 w-4" />
            <span>So sánh BOM</span>
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button
                variant="secondary"
                onClick={() => window.print()}
                className="flex items-center gap-1.5"
              >
                <Printer className="h-4 w-4" />
                <span>In / PDF</span>
              </Button>

              <Button
                variant="secondary"
                onClick={() => handleExportExcel(bom)}
                className="flex items-center gap-1.5 text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
              >
                <FileSpreadsheet className="h-4 w-4" />
                <span>Xuất Excel</span>
              </Button>

              {canWrite && (
                <Button
                  variant="secondary"
                  onClick={handleOpenCloneModal}
                  className="flex items-center gap-1.5 text-indigo-700 bg-indigo-50 border-indigo-200 hover:bg-indigo-100"
                >
                  <Copy className="h-4 w-4" />
                  <span>Nhân bản BOM</span>
                </Button>
              )}

              {canWrite && (product?.id || bom.product_id) && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    navigate(
                      `/purchasing/boms/${product?.id || bom.product_id}/master`,
                    )
                  }
                  className="flex items-center gap-1.5 text-purple-700 bg-purple-50 border-purple-200 hover:bg-purple-100"
                >
                  <Layers className="h-4 w-4" />
                  <span>Master BOM (Kỹ thuật)</span>
                </Button>
              )}

              {bom.status === 'inactive' && canActivate && (
                <Button
                  variant="primary"
                  onClick={handleActivate}
                  disabled={saving}
                  className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Kích hoạt BOM</span>
                </Button>
              )}

              {canWrite ? (
                <Button
                  variant="primary"
                  onClick={handleStartEdit}
                  className="flex items-center gap-1.5"
                >
                  <Pencil className="h-4 w-4" />
                  <span>Chỉnh sửa BOM</span>
                </Button>
              ) : (
                <span
                  className="text-xs text-slate-400 italic"
                  title="Chỉ tài khoản có quyền bom.write mới được sửa"
                >
                  (Không có quyền sửa)
                </span>
              )}
            </>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={handleCancelEdit}
                disabled={saving}
                className="flex items-center gap-1.5"
              >
                <X className="h-4 w-4" />
                <span>Hủy bỏ</span>
              </Button>

              <Button
                variant="primary"
                onClick={handleSaveActive}
                disabled={saving}
                className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700"
              >
                <Save className="h-4 w-4" />
                <span>{saving ? 'Đang lưu...' : 'Lưu phiên bản mới'}</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* 5b. Print view for old historical version */}
      {printOldBom && (
        <div className="hidden print:block">
          <BomSheet
            itemCode={product?.code || printOldBom.code_hint || printOldBom.name}
            productName={product?.name || printOldBom.name}
            productDimensions={
              product
                ? `${product.w ?? '—'} × ${product.d ?? '—'} × ${product.h ?? '—'} mm`
                : undefined
            }
            variantColor={product?.name ? undefined : 'Mặc định'}
            assemblyType={printOldBom.assembly_type}
            mainWoodType={printOldBom.woodLines[0]?.wood_type_raw || 'Theo chi tiết'}
            veneerSpec={
              printOldBom.woodLines.find((w) => w.veneer_spec)?.veneer_spec || '—'
            }
            bomCode={printOldBom.code_hint || printOldBom.name}
            versionSeq={printOldBom.version_seq}
            editCount={printOldBom.edit_count ?? 0}
            status={printOldBom.status}
            updatedAt={printOldBom.updated_at}
            preparedBy={printOldBom.prepared_by}
            checkedBy={printOldBom.checked_by}
            checkedBy2={printOldBom.checked_by_2}
            approvedBy={printOldBom.approved_by}
            signedOn={printOldBom.signed_on}
            notes={printOldBom.notes}
            woodLines={printOldBom.woodLines}
            hardwareLines={printOldBom.hardwareLines}
          />
        </div>
      )}

      {/* 5. Main View: Read-only BomSheet OR In-place Edit Form */}
      <div className={printOldBom ? 'print:hidden' : ''}>
      {!isEditing ? (
        <BomSheet
          itemCode={itemCode}
          productName={productName}
          productDimensions={productDimensions}
          variantColor={product?.name ? undefined : 'Mặc định'}
          assemblyType={bom.assembly_type}
          mainWoodType={bom.woodLines[0]?.wood_type_raw || 'Theo chi tiết'}
          veneerSpec={bom.woodLines.find((w) => w.veneer_spec)?.veneer_spec || '—'}
          bomCode={bom.code_hint || bom.name}
          versionSeq={bom.version_seq}
          editCount={bom.edit_count ?? 0}
          status={bom.status}
          updatedAt={bom.updated_at}
          preparedBy={bom.prepared_by}
          checkedBy={bom.checked_by}
          checkedBy2={bom.checked_by_2}
          approvedBy={bom.approved_by}
          signedOn={bom.signed_on}
          notes={bom.notes}
          woodLines={bom.woodLines}
          hardwareLines={bom.hardwareLines}
        />
      ) : (
        /* In-place Edit Form */
        <div className="space-y-6 rounded-xl border border-indigo-200 bg-white p-6 shadow-sm">
          {/* Edit header notification */}
          <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-4 text-xs text-indigo-900 flex items-start gap-3">
            <Pencil className="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-sm text-indigo-950">
                Chế độ chỉnh sửa trực tiếp — Bản sao nháp
              </h3>
              <p className="mt-1">
                Lưu lại sẽ tự động tạo phiên bản mới (
                <strong>{`v${bom.version_seq + 1}`}</strong> · Đã sửa lần{' '}
                <strong>{(bom.edit_count ?? 0) + 1}</strong>) và đưa bản hiện tại (v
                {bom.version_seq}) vào kho lưu trữ "BOM cũ".
              </p>
              <div className="mt-3">
                <label className="block font-medium text-slate-700 mb-1">
                  Ghi chú lần sửa (tùy chọn):
                </label>
                <input
                  type="text"
                  value={draftEditNote}
                  onChange={(e) => setDraftEditNote(e.target.value)}
                  placeholder="Ví dụ: Thay đổi kích thước chân bàn sang 45x45mm"
                  className="w-full max-w-xl rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-900 outline-none focus:border-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Bảng 1: Sửa Gỗ */}
          <section className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between bg-slate-900 px-6 py-3 text-white">
              <div className="flex items-center space-x-2">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                <h3 className="text-xs font-bold tracking-wider uppercase">
                  Bảng 1 — Chỉnh sửa chi tiết GỖ ({draftWoodLines.length} dòng)
                </h3>
              </div>
              <span className="text-xs text-slate-300">Đơn vị: 01 sản phẩm</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-100 text-slate-700 font-semibold">
                    <th className="py-2 px-3 w-8 text-center">
                      <input
                        type="checkbox"
                        checked={
                          draftWoodLines.length > 0 &&
                          selectedWoodIndices.size === draftWoodLines.length
                        }
                        onChange={handleToggleSelectAllWood}
                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        title="Chọn tất cả dòng"
                      />
                    </th>
                    <th className="py-2 px-3 w-10 text-center">STT</th>
                    <th className="py-2 px-3 min-w-[180px]">Tên chi tiết *</th>
                    <th className="py-2 px-3 min-w-[120px]">Loại gỗ / phôi</th>
                    <th className="py-2 px-3 w-20">Dày (mm)</th>
                    <th className="py-2 px-3 w-20">Rộng (mm)</th>
                    <th className="py-2 px-3 w-20">Dài (mm)</th>
                    <th className="py-2 px-3 w-20">Mộng</th>
                    <th className="py-2 px-3 w-16 text-center">SL/SP *</th>
                    <th className="py-2 px-3 w-24 text-right">M³ tinh</th>
                    <th className="py-2 px-3 min-w-[120px]">Veneer</th>
                    <th className="py-2 px-3 min-w-[140px]">Ghi chú</th>
                    <th className="py-2 px-3 w-16 text-center">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {draftWoodLines.map((line, idx) => {
                    const validation = validateWoodLine(line)
                    const isSelected = selectedWoodIndices.has(idx)

                    return (
                      <tr
                        key={line.id || idx}
                        className={`hover:bg-slate-50 transition-colors ${
                          isSelected ? 'bg-indigo-50/60' : ''
                        }`}
                      >
                        <td className="py-1.5 px-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelectWoodLine(idx)}
                            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                        <td className="py-1.5 px-3 text-center text-slate-400 font-mono">
                          {idx + 1}
                        </td>
                        <td className="py-1.5 px-2">
                          <input
                            type="text"
                            data-table="wood"
                            data-row={idx}
                            data-col={0}
                            value={line.part_name}
                            onChange={(e) =>
                              handleUpdateWoodLine(idx, 'part_name', e.target.value)
                            }
                            onKeyDown={(e) =>
                              handleCellKeyDown(e, 'wood', idx, 0, draftWoodLines.length)
                            }
                            onPaste={(e) => handleCellPaste(e, 'wood', idx)}
                            className={`w-full rounded border px-2 py-1 text-xs outline-none focus:ring-1 ${
                              validation.errors.part_name
                                ? 'border-rose-400 bg-rose-50/50 focus:border-rose-500 focus:ring-rose-500'
                                : 'border-slate-200 focus:border-indigo-500 focus:ring-indigo-500'
                            }`}
                            placeholder="Tên chi tiết..."
                            title={validation.errors.part_name}
                          />
                        </td>
                        <td className="py-1.5 px-2">
                          <input
                            type="text"
                            data-table="wood"
                            data-row={idx}
                            data-col={1}
                            value={line.wood_type_raw || ''}
                            onChange={(e) =>
                              handleUpdateWoodLine(idx, 'wood_type_raw', e.target.value)
                            }
                            onKeyDown={(e) =>
                              handleCellKeyDown(e, 'wood', idx, 1, draftWoodLines.length)
                            }
                            onPaste={(e) => handleCellPaste(e, 'wood', idx)}
                            className="w-full rounded border border-slate-200 px-2 py-1 text-xs focus:border-indigo-500"
                            placeholder="Cao su, MDF..."
                          />
                        </td>
                        <td className="py-1.5 px-2">
                          <input
                            type="number"
                            step="any"
                            data-table="wood"
                            data-row={idx}
                            data-col={2}
                            value={line.dims_finished_t ?? ''}
                            onChange={(e) =>
                              handleUpdateWoodLine(
                                idx,
                                'dims_finished_t',
                                e.target.value ? Number(e.target.value) : null,
                              )
                            }
                            onKeyDown={(e) =>
                              handleCellKeyDown(e, 'wood', idx, 2, draftWoodLines.length)
                            }
                            onPaste={(e) => handleCellPaste(e, 'wood', idx)}
                            className={`w-full rounded border px-2 py-1 text-xs text-right focus:border-indigo-500 ${
                              validation.errors.dims
                                ? 'border-amber-300 bg-amber-50/30'
                                : 'border-slate-200'
                            }`}
                          />
                        </td>
                        <td className="py-1.5 px-2">
                          <input
                            type="number"
                            step="any"
                            data-table="wood"
                            data-row={idx}
                            data-col={3}
                            value={line.dims_finished_w ?? ''}
                            onChange={(e) =>
                              handleUpdateWoodLine(
                                idx,
                                'dims_finished_w',
                                e.target.value ? Number(e.target.value) : null,
                              )
                            }
                            onKeyDown={(e) =>
                              handleCellKeyDown(e, 'wood', idx, 3, draftWoodLines.length)
                            }
                            onPaste={(e) => handleCellPaste(e, 'wood', idx)}
                            className={`w-full rounded border px-2 py-1 text-xs text-right focus:border-indigo-500 ${
                              validation.errors.dims
                                ? 'border-amber-300 bg-amber-50/30'
                                : 'border-slate-200'
                            }`}
                          />
                        </td>
                        <td className="py-1.5 px-2">
                          <input
                            type="number"
                            step="any"
                            data-table="wood"
                            data-row={idx}
                            data-col={4}
                            value={line.dims_finished_l ?? ''}
                            onChange={(e) =>
                              handleUpdateWoodLine(
                                idx,
                                'dims_finished_l',
                                e.target.value ? Number(e.target.value) : null,
                              )
                            }
                            onKeyDown={(e) =>
                              handleCellKeyDown(e, 'wood', idx, 4, draftWoodLines.length)
                            }
                            onPaste={(e) => handleCellPaste(e, 'wood', idx)}
                            className={`w-full rounded border px-2 py-1 text-xs text-right focus:border-indigo-500 ${
                              validation.errors.dims
                                ? 'border-amber-300 bg-amber-50/30'
                                : 'border-slate-200'
                            }`}
                          />
                        </td>
                        <td className="py-1.5 px-2">
                          <input
                            type="text"
                            data-table="wood"
                            data-row={idx}
                            data-col={5}
                            value={line.tenon || ''}
                            onChange={(e) =>
                              handleUpdateWoodLine(idx, 'tenon', e.target.value)
                            }
                            onKeyDown={(e) =>
                              handleCellKeyDown(e, 'wood', idx, 5, draftWoodLines.length)
                            }
                            onPaste={(e) => handleCellPaste(e, 'wood', idx)}
                            className="w-full rounded border border-slate-200 px-2 py-1 text-xs focus:border-indigo-500"
                          />
                        </td>
                        <td className="py-1.5 px-2">
                          <input
                            type="number"
                            min="1"
                            data-table="wood"
                            data-row={idx}
                            data-col={6}
                            value={line.qty_per}
                            onChange={(e) =>
                              handleUpdateWoodLine(idx, 'qty_per', Number(e.target.value))
                            }
                            onKeyDown={(e) =>
                              handleCellKeyDown(e, 'wood', idx, 6, draftWoodLines.length)
                            }
                            onPaste={(e) => handleCellPaste(e, 'wood', idx)}
                            className={`w-full rounded border px-2 py-1 text-xs text-center font-bold focus:border-indigo-500 ${
                              validation.errors.qty_per
                                ? 'border-rose-400 bg-rose-50/50 text-rose-700'
                                : 'border-slate-200 text-slate-800'
                            }`}
                          />
                        </td>
                        <td className="py-1.5 px-3 text-right font-mono text-xs">
                          {validation.m3Finished != null ? (
                            <span className="font-semibold text-slate-700">
                              {validation.m3Finished.toFixed(4)}
                            </span>
                          ) : (
                            <span className="text-amber-600 font-medium text-xs italic">
                              Thiếu KT
                            </span>
                          )}
                        </td>
                        <td className="py-1.5 px-2">
                          <input
                            type="text"
                            data-table="wood"
                            data-row={idx}
                            data-col={7}
                            value={line.veneer_spec || ''}
                            onChange={(e) =>
                              handleUpdateWoodLine(idx, 'veneer_spec', e.target.value)
                            }
                            onKeyDown={(e) =>
                              handleCellKeyDown(e, 'wood', idx, 7, draftWoodLines.length)
                            }
                            onPaste={(e) => handleCellPaste(e, 'wood', idx)}
                            className="w-full rounded border border-slate-200 px-2 py-1 text-xs focus:border-indigo-500"
                          />
                        </td>
                        <td className="py-1.5 px-2">
                          <input
                            type="text"
                            data-table="wood"
                            data-row={idx}
                            data-col={8}
                            value={line.notes || ''}
                            onChange={(e) =>
                              handleUpdateWoodLine(idx, 'notes', e.target.value)
                            }
                            onKeyDown={(e) =>
                              handleCellKeyDown(e, 'wood', idx, 8, draftWoodLines.length)
                            }
                            onPaste={(e) => handleCellPaste(e, 'wood', idx)}
                            className="w-full rounded border border-slate-200 px-2 py-1 text-xs focus:border-indigo-500"
                          />
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleDuplicateWoodLine(idx)}
                              className="text-indigo-600 hover:text-indigo-800 p-1 rounded hover:bg-indigo-50"
                              title="Nhân bản dòng (Ctrl+D)"
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveWoodLine(idx)}
                              className="text-rose-500 hover:text-rose-700 p-1 rounded hover:bg-rose-50"
                              title="Xóa dòng"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="p-3 bg-slate-50 border-t border-slate-200 flex flex-wrap justify-between items-center gap-3">
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  onClick={handleAddWoodLine}
                  className="text-xs flex items-center gap-1.5"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>Thêm dòng gỗ</span>
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleOpenPasteModal('wood', draftWoodLines.length)}
                  className="text-xs flex items-center gap-1.5 text-indigo-700 bg-indigo-50 border-indigo-200 hover:bg-indigo-100"
                >
                  <FileSpreadsheet className="h-3.5 w-3.5" />
                  <span>Dán từ Excel</span>
                </Button>
                {selectedWoodIndices.size > 0 && (
                  <Button
                    variant="danger"
                    onClick={handleBulkDeleteWood}
                    className="text-xs flex items-center gap-1.5"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    <span>Xóa {selectedWoodIndices.size} dòng đã chọn</span>
                  </Button>
                )}
              </div>
              <span className="text-xs text-slate-500 italic">
                * Phím tắt: Enter xuống dòng, Tab/Mũi tên chuyển ô, Ctrl+D nhân bản, Ctrl+V dán Excel
              </span>
            </div>
          </section>

          {/* Bảng 2: Sửa Ngũ Kim */}
          <section className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between bg-slate-900 px-6 py-3 text-white">
              <div className="flex items-center space-x-2">
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                <h3 className="text-xs font-bold tracking-wider uppercase">
                  Bảng 2 — Chỉnh sửa NGŨ KIM ({draftHardwareLines.length} dòng)
                </h3>
              </div>
              <span className="text-xs text-slate-300">
                Mode: {bom.assembly_type === 'assembled' ? 'RÁP CHẾT (FA)' : 'Tháo rời (KD)'}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-100 text-slate-700 font-semibold">
                    <th className="py-2 px-3 w-8 text-center">
                      <input
                        type="checkbox"
                        checked={
                          draftHardwareLines.length > 0 &&
                          selectedHardwareIndices.size === draftHardwareLines.length
                        }
                        onChange={handleToggleSelectAllHardware}
                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        title="Chọn tất cả dòng"
                      />
                    </th>
                    <th className="py-2 px-3 w-10 text-center">STT</th>
                    <th className="py-2 px-3 min-w-[180px]">Tên vật tư / quy cách *</th>
                    <th className="py-2 px-3 min-w-[140px]">Quy cách kỹ thuật</th>
                    <th className="py-2 px-3 min-w-[160px]">Vật tư kho map</th>
                    <th className="py-2 px-3 w-20 text-center">SL/SP *</th>
                    <th className="py-2 px-3 w-24">Đơn vị</th>
                    <th className="py-2 px-3 w-28">Màu sắc</th>
                    <th className="py-2 px-3 min-w-[180px]">Vị trí / Ghi chú</th>
                    <th className="py-2 px-3 w-16 text-center">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {draftHardwareLines.map((line, idx) => {
                    const validation = validateHardwareLine(line)
                    const isSelected = selectedHardwareIndices.has(idx)

                    return (
                      <tr
                        key={line.id || idx}
                        className={`hover:bg-slate-50 transition-colors ${
                          isSelected ? 'bg-indigo-50/60' : ''
                        }`}
                      >
                        <td className="py-1.5 px-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelectHardwareLine(idx)}
                            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                        <td className="py-1.5 px-3 text-center text-slate-400 font-mono">
                          {idx + 1}
                        </td>
                        <td className="py-1.5 px-2">
                          <input
                            type="text"
                            data-table="hardware"
                            data-row={idx}
                            data-col={0}
                            value={line.name_raw}
                            onChange={(e) =>
                              handleUpdateHardwareLine(idx, 'name_raw', e.target.value)
                            }
                            onKeyDown={(e) =>
                              handleCellKeyDown(e, 'hardware', idx, 0, draftHardwareLines.length)
                            }
                            onPaste={(e) => handleCellPaste(e, 'hardware', idx)}
                            className={`w-full rounded border px-2 py-1 text-xs outline-none focus:ring-1 ${
                              validation.errors.name_raw
                                ? 'border-rose-400 bg-rose-50/50 focus:border-rose-500 focus:ring-rose-500'
                                : 'border-slate-200 focus:border-indigo-500 focus:ring-indigo-500'
                            }`}
                            placeholder="Ví dụ: Bản lề giảm chấn..."
                            title={validation.errors.name_raw}
                          />
                        </td>
                        <td className="py-1.5 px-2">
                          <input
                            type="text"
                            data-table="hardware"
                            data-row={idx}
                            data-col={1}
                            value={line.spec_raw || ''}
                            onChange={(e) =>
                              handleUpdateHardwareLine(idx, 'spec_raw', e.target.value)
                            }
                            onKeyDown={(e) =>
                              handleCellKeyDown(e, 'hardware', idx, 1, draftHardwareLines.length)
                            }
                            onPaste={(e) => handleCellPaste(e, 'hardware', idx)}
                            className="w-full rounded border border-slate-200 px-2 py-1 text-xs focus:border-indigo-500"
                            placeholder="Phi 35, cong vừa..."
                          />
                        </td>
                        <td className="py-1.5 px-2">
                          <select
                            value={line.material_id || ''}
                            onChange={(e) =>
                              handleUpdateHardwareLine(
                                idx,
                                'material_id',
                                e.target.value || null,
                              )
                            }
                            className="w-full rounded border border-slate-200 px-2 py-1 text-xs focus:border-indigo-500 bg-white"
                          >
                            <option value="">(Chưa map vật tư)</option>
                            {materials.map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.name} ({m.uom_default || '—'})
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-1.5 px-2">
                          <input
                            type="number"
                            min="1"
                            data-table="hardware"
                            data-row={idx}
                            data-col={2}
                            value={line.qty_per}
                            onChange={(e) =>
                              handleUpdateHardwareLine(idx, 'qty_per', Number(e.target.value))
                            }
                            onKeyDown={(e) =>
                              handleCellKeyDown(e, 'hardware', idx, 2, draftHardwareLines.length)
                            }
                            onPaste={(e) => handleCellPaste(e, 'hardware', idx)}
                            className={`w-full rounded border px-2 py-1 text-xs text-center font-bold focus:border-indigo-500 ${
                              validation.errors.qty_per
                                ? 'border-rose-400 bg-rose-50/50 text-rose-700'
                                : 'border-slate-200 text-slate-800'
                            }`}
                          />
                        </td>
                        <td className="py-1.5 px-2">
                          <input
                            type="text"
                            data-table="hardware"
                            data-row={idx}
                            data-col={3}
                            value={line.uom_hint || ''}
                            onChange={(e) =>
                              handleUpdateHardwareLine(idx, 'uom_hint', e.target.value)
                            }
                            onKeyDown={(e) =>
                              handleCellKeyDown(e, 'hardware', idx, 3, draftHardwareLines.length)
                            }
                            onPaste={(e) => handleCellPaste(e, 'hardware', idx)}
                            className="w-full rounded border border-slate-200 px-2 py-1 text-xs focus:border-indigo-500"
                            placeholder="Cái, Bộ, Con..."
                          />
                        </td>
                        <td className="py-1.5 px-2">
                          <input
                            type="text"
                            data-table="hardware"
                            data-row={idx}
                            data-col={4}
                            value={line.color_hint || ''}
                            onChange={(e) =>
                              handleUpdateHardwareLine(idx, 'color_hint', e.target.value)
                            }
                            onKeyDown={(e) =>
                              handleCellKeyDown(e, 'hardware', idx, 4, draftHardwareLines.length)
                            }
                            onPaste={(e) => handleCellPaste(e, 'hardware', idx)}
                            className="w-full rounded border border-slate-200 px-2 py-1 text-xs focus:border-indigo-500"
                            placeholder="Đen, Mạ niken..."
                          />
                        </td>
                        <td className="py-1.5 px-2">
                          <input
                            type="text"
                            data-table="hardware"
                            data-row={idx}
                            data-col={5}
                            value={line.notes || ''}
                            onChange={(e) =>
                              handleUpdateHardwareLine(idx, 'notes', e.target.value)
                            }
                            onKeyDown={(e) =>
                              handleCellKeyDown(e, 'hardware', idx, 5, draftHardwareLines.length)
                            }
                            onPaste={(e) => handleCellPaste(e, 'hardware', idx)}
                            className="w-full rounded border border-slate-200 px-2 py-1 text-xs focus:border-indigo-500"
                            placeholder="Lắp cánh tủ..."
                          />
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleDuplicateHardwareLine(idx)}
                              className="text-indigo-600 hover:text-indigo-800 p-1 rounded hover:bg-indigo-50"
                              title="Nhân bản dòng (Ctrl+D)"
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveHardwareLine(idx)}
                              className="text-rose-500 hover:text-rose-700 p-1 rounded hover:bg-rose-50"
                              title="Xóa dòng"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="p-3 bg-slate-50 border-t border-slate-200 flex flex-wrap justify-between items-center gap-3">
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  onClick={handleAddHardwareLine}
                  className="text-xs flex items-center gap-1.5"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>Thêm dòng ngũ kim</span>
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleOpenPasteModal('hardware', draftHardwareLines.length)}
                  className="text-xs flex items-center gap-1.5 text-indigo-700 bg-indigo-50 border-indigo-200 hover:bg-indigo-100"
                >
                  <FileSpreadsheet className="h-3.5 w-3.5" />
                  <span>Dán từ Excel</span>
                </Button>
                {selectedHardwareIndices.size > 0 && (
                  <Button
                    variant="danger"
                    onClick={handleBulkDeleteHardware}
                    className="text-xs flex items-center gap-1.5"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    <span>Xóa {selectedHardwareIndices.size} dòng đã chọn</span>
                  </Button>
                )}
              </div>
              <span className="text-xs text-slate-500 italic">
                * Phím tắt: Enter xuống dòng, Tab/Mũi tên chuyển ô, Ctrl+D nhân bản, Ctrl+V dán Excel
              </span>
            </div>
          </section>

          {/* Bottom actions for Edit mode */}
          <div className="flex justify-end gap-3 pt-3 border-t border-slate-200">
            <Button
              variant="secondary"
              onClick={handleCancelEdit}
              disabled={saving}
              className="flex items-center gap-1.5"
            >
              <X className="h-4 w-4" />
              <span>Hủy bỏ</span>
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveActive}
              disabled={saving}
              className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 px-5"
            >
              <Save className="h-4 w-4" />
              <span>{saving ? 'Đang lưu...' : 'Lưu phiên bản mới'}</span>
            </Button>
          </div>
        </div>
      )}
      </div>

      {/* 6. History Drawer ("BOM cũ") */}
      <div className="print:hidden">
      <Drawer
        open={showHistory}
        onClose={() => {
          setShowHistory(false)
          setSelectedHistoryBom(null)
          setShowDiff(false)
        }}
        title="Lịch sử phiên bản — Thư mục 'BOM cũ'"
        widthClass="w-full max-w-4xl"
      >
        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(100vh-100px)]">
          {!selectedHistoryBom ? (
            /* Versions list */
            <div>
              <p className="text-xs text-slate-500 mb-4">
                Danh sách các phiên bản đã lưu trữ của BOM này. Mọi thay đổi qua các lần sửa
                đều được ghi lại bất biến.
              </p>

              {historyLoading ? (
                <div className="text-center py-8 text-slate-400 text-sm">
                  Đang tải danh sách phiên bản...
                </div>
              ) : familyVersions.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">
                  Không tìm thấy phiên bản nào khác.
                </div>
              ) : (
                <div className="divide-y divide-slate-200 border border-slate-200 rounded-xl bg-white overflow-hidden shadow-sm">
                  {familyVersions.map((v) => {
                    const isCurrentActive = v.id === bom.id

                    return (
                      <div
                        key={v.id}
                        className={`p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                          isCurrentActive ? 'bg-indigo-50/50' : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-sm text-slate-900">
                              {`v${v.version_seq}`}
                            </span>
                            <StatusBadge
                              tone={
                                v.status === 'active'
                                  ? 'success'
                                  : v.status === 'archived'
                                    ? 'neutral'
                                    : 'warning'
                              }
                              label={
                                v.status === 'active'
                                  ? 'Đang dùng (ACTIVE)'
                                  : v.status === 'archived'
                                    ? 'Lưu trữ (ARCHIVED)'
                                    : 'Bản nháp'
                              }
                            />
                            {isCurrentActive && (
                              <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-xs font-semibold text-indigo-800">
                                Bản đang mở
                              </span>
                            )}
                          </div>

                          <div className="text-xs text-slate-500 flex flex-wrap gap-x-3">
                            <span>
                              {v.edit_count && v.edit_count > 0
                                ? `Đã sửa lần ${v.edit_count}`
                                : 'Bản khởi tạo'}
                            </span>
                            {v.edited_by && (
                              <span>Người sửa: <strong>{v.edited_by}</strong></span>
                            )}
                            <span>
                              {new Date(v.edited_at || v.created_at).toLocaleString('vi-VN')}
                            </span>
                          </div>

                          {v.edit_note && (
                            <p className="text-xs text-slate-600 bg-slate-100 p-2 rounded mt-1 border border-slate-200">
                              <span className="font-semibold">Ghi chú:</span> {v.edit_note}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-2 self-end sm:self-center">
                          <Button
                            variant="secondary"
                            className="text-xs"
                            onClick={() => handleSelectHistoryVersion(v.id)}
                          >
                            Xem chi tiết bản này
                          </Button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          ) : (
            /* Selected historical version view */
            <div className="space-y-4">
              {/* Back to list & Compare toggle toolbar */}
              <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-100 p-3 rounded-lg border border-slate-200">
                <Button
                  variant="secondary"
                  className="text-xs flex items-center gap-1.5"
                  onClick={() => {
                    setSelectedHistoryBom(null)
                    setShowDiff(false)
                  }}
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  <span>Quay lại danh sách BOM cũ</span>
                </Button>

                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    className="text-xs flex items-center gap-1.5 text-emerald-700 bg-white border-slate-200 hover:bg-slate-50"
                    onClick={() => handleExportExcel(selectedHistoryBom)}
                  >
                    <FileSpreadsheet className="h-3.5 w-3.5" />
                    <span>Xuất Excel (bản cũ v{selectedHistoryBom.version_seq})</span>
                  </Button>

                  <Button
                    variant="secondary"
                    className="text-xs flex items-center gap-1.5 text-slate-700 bg-white border-slate-200 hover:bg-slate-50"
                    onClick={() => handlePrintOldVersion(selectedHistoryBom)}
                  >
                    <Printer className="h-3.5 w-3.5" />
                    <span>In / PDF (bản cũ v{selectedHistoryBom.version_seq})</span>
                  </Button>

                  <Button
                    variant="secondary"
                    className={`text-xs flex items-center gap-1.5 ${
                      showDiff
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                        : 'bg-white text-slate-800 hover:bg-slate-50'
                    }`}
                    onClick={() => setShowDiff(!showDiff)}
                  >
                    <GitCompare className="h-3.5 w-3.5" />
                    <span>
                      {showDiff ? 'Xem phiếu đầy đủ' : `So sánh với bản hiện tại (v${bom.version_seq})`}
                    </span>
                  </Button>
                </div>
              </div>

              {selectedHistoryLoading ? (
                <div className="text-center py-12 text-slate-400 text-sm">
                  Đang tải chi tiết phiên bản cũ...
                </div>
              ) : showDiff && diffResult ? (
                /* Comparison view */
                <div className="space-y-6">
                  <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-xs text-indigo-900">
                    <h4 className="font-bold text-sm">
                      Kết quả so sánh: Phiên bản hiện tại (v{bom.version_seq}) so với bản cũ (v
                      {selectedHistoryBom.version_seq})
                    </h4>
                    <p className="mt-1">
                      Ký hiệu: <span className="text-emerald-700 font-bold">[+] Mới thêm</span> ·{' '}
                      <span className="text-rose-700 font-bold">[-] Đã xóa</span> ·{' '}
                      <span className="text-amber-700 font-bold">[~] Thay đổi thông số</span> ·{' '}
                      <span>[=] Không đổi</span>
                    </p>
                  </div>

                  {/* Wood comparison */}
                  <div className="space-y-2">
                    <h5 className="font-bold text-xs uppercase tracking-wider text-slate-700">
                      1. So sánh chi tiết GỖ ({diffResult.wood.added.length} thêm,{' '}
                      {diffResult.wood.removed.length} bớt,{' '}
                      {diffResult.wood.modified.length} sửa,{' '}
                      {diffResult.wood.unchanged.length} giữ nguyên)
                    </h5>

                    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white text-xs">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-slate-100 border-b border-slate-200 font-semibold text-slate-700">
                            <th className="py-2 px-3 w-16">Trạng thái</th>
                            <th className="py-2 px-3">Chi tiết</th>
                            <th className="py-2 px-3">Loại gỗ</th>
                            <th className="py-2 px-3">Kích thước (D×R×D)</th>
                            <th className="py-2 px-3 w-16 text-center">SL</th>
                            <th className="py-2 px-3">Chi tiết sai khác</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {/* Added */}
                          {diffResult.wood.added.map((line, idx) => (
                            <tr key={`add-${idx}`} className="bg-emerald-50/70 text-emerald-950 font-medium">
                              <td className="py-1.5 px-3 text-emerald-700 font-bold">+ Thêm</td>
                              <td className="py-1.5 px-3">{line.part_name}</td>
                              <td className="py-1.5 px-3">{line.wood_type_raw || '—'}</td>
                              <td className="py-1.5 px-3 font-mono">
                                {`${line.dims_finished_t ?? '—'} × ${line.dims_finished_w ?? '—'} × ${line.dims_finished_l ?? '—'}`}
                              </td>
                              <td className="py-1.5 px-3 text-center font-bold">{line.qty_per}</td>
                              <td className="py-1.5 px-3 italic text-emerald-700">Chỉ có ở bản hiện tại</td>
                            </tr>
                          ))}

                          {/* Removed */}
                          {diffResult.wood.removed.map((line, idx) => (
                            <tr key={`rem-${idx}`} className="bg-rose-50/70 text-rose-950 line-through">
                              <td className="py-1.5 px-3 text-rose-700 font-bold">- Đã xóa</td>
                              <td className="py-1.5 px-3">{line.part_name}</td>
                              <td className="py-1.5 px-3">{line.wood_type_raw || '—'}</td>
                              <td className="py-1.5 px-3 font-mono">
                                {`${line.dims_finished_t ?? '—'} × ${line.dims_finished_w ?? '—'} × ${line.dims_finished_l ?? '—'}`}
                              </td>
                              <td className="py-1.5 px-3 text-center">{line.qty_per}</td>
                              <td className="py-1.5 px-3 italic text-rose-700 not-line-through">Đã bị bỏ ở bản hiện tại</td>
                            </tr>
                          ))}

                          {/* Modified */}
                          {diffResult.wood.modified.map(({ current, previous, diffs }, idx) => (
                            <tr key={`mod-${idx}`} className="bg-amber-50/70 text-amber-950">
                              <td className="py-1.5 px-3 text-amber-700 font-bold">~ Sửa</td>
                              <td className="py-1.5 px-3 font-medium">{current.part_name}</td>
                              <td className="py-1.5 px-3">{current.wood_type_raw || '—'}</td>
                              <td className="py-1.5 px-3 font-mono">
                                <span className="line-through text-slate-400">
                                  {`${previous.dims_finished_t ?? '—'}×${previous.dims_finished_w ?? '—'}×${previous.dims_finished_l ?? '—'}`}
                                </span>
                                {' → '}
                                <span className="font-bold text-slate-900">
                                  {`${current.dims_finished_t ?? '—'}×${current.dims_finished_w ?? '—'}×${current.dims_finished_l ?? '—'}`}
                                </span>
                              </td>
                              <td className="py-1.5 px-3 text-center font-bold">
                                {previous.qty_per !== current.qty_per ? (
                                  <>
                                    <span className="line-through text-slate-400">{previous.qty_per}</span>
                                    {' → '}
                                    <span>{current.qty_per}</span>
                                  </>
                                ) : (
                                  current.qty_per
                                )}
                              </td>
                              <td className="py-1.5 px-3 text-amber-800">
                                Thay đổi: <strong>{diffs.join(', ')}</strong>
                              </td>
                            </tr>
                          ))}

                          {/* Unchanged count */}
                          {diffResult.wood.unchanged.length > 0 && (
                            <tr className="bg-slate-50 text-slate-500">
                              <td colSpan={6} className="py-2 px-3 text-center italic">
                                ... và {diffResult.wood.unchanged.length} chi tiết gỗ giống nhau hoàn toàn
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Hardware comparison */}
                  <div className="space-y-2">
                    <h5 className="font-bold text-xs uppercase tracking-wider text-slate-700">
                      2. So sánh chi tiết NGŨ KIM ({diffResult.hardware.added.length} thêm,{' '}
                      {diffResult.hardware.removed.length} bớt,{' '}
                      {diffResult.hardware.modified.length} sửa,{' '}
                      {diffResult.hardware.unchanged.length} giữ nguyên)
                    </h5>

                    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white text-xs">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-slate-100 border-b border-slate-200 font-semibold text-slate-700">
                            <th className="py-2 px-3 w-16">Trạng thái</th>
                            <th className="py-2 px-3">Tên vật tư</th>
                            <th className="py-2 px-3">Quy cách kỹ thuật</th>
                            <th className="py-2 px-3 w-16 text-center">SL</th>
                            <th className="py-2 px-3 w-20">Đơn vị</th>
                            <th className="py-2 px-3">Chi tiết sai khác</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {/* Added */}
                          {diffResult.hardware.added.map((line, idx) => (
                            <tr key={`hw-add-${idx}`} className="bg-emerald-50/70 text-emerald-950 font-medium">
                              <td className="py-1.5 px-3 text-emerald-700 font-bold">+ Thêm</td>
                              <td className="py-1.5 px-3">{line.name_raw}</td>
                              <td className="py-1.5 px-3">{line.spec_raw || '—'}</td>
                              <td className="py-1.5 px-3 text-center font-bold">{line.qty_per}</td>
                              <td className="py-1.5 px-3">{line.uom_hint || '—'}</td>
                              <td className="py-1.5 px-3 italic text-emerald-700">Chỉ có ở bản hiện tại</td>
                            </tr>
                          ))}

                          {/* Removed */}
                          {diffResult.hardware.removed.map((line, idx) => (
                            <tr key={`hw-rem-${idx}`} className="bg-rose-50/70 text-rose-950 line-through">
                              <td className="py-1.5 px-3 text-rose-700 font-bold">- Đã xóa</td>
                              <td className="py-1.5 px-3">{line.name_raw}</td>
                              <td className="py-1.5 px-3">{line.spec_raw || '—'}</td>
                              <td className="py-1.5 px-3 text-center">{line.qty_per}</td>
                              <td className="py-1.5 px-3">{line.uom_hint || '—'}</td>
                              <td className="py-1.5 px-3 italic text-rose-700 not-line-through">Đã bị bỏ ở bản hiện tại</td>
                            </tr>
                          ))}

                          {/* Modified */}
                          {diffResult.hardware.modified.map(({ current, previous, diffs }, idx) => (
                            <tr key={`hw-mod-${idx}`} className="bg-amber-50/70 text-amber-950">
                              <td className="py-1.5 px-3 text-amber-700 font-bold">~ Sửa</td>
                              <td className="py-1.5 px-3 font-medium">{current.name_raw}</td>
                              <td className="py-1.5 px-3">{current.spec_raw || '—'}</td>
                              <td className="py-1.5 px-3 text-center font-bold">
                                {previous.qty_per !== current.qty_per ? (
                                  <>
                                    <span className="line-through text-slate-400">{previous.qty_per}</span>
                                    {' → '}
                                    <span>{current.qty_per}</span>
                                  </>
                                ) : (
                                  current.qty_per
                                )}
                              </td>
                              <td className="py-1.5 px-3">{current.uom_hint || '—'}</td>
                              <td className="py-1.5 px-3 text-amber-800">
                                Thay đổi: <strong>{diffs.join(', ')}</strong>
                              </td>
                            </tr>
                          ))}

                          {/* Unchanged count */}
                          {diffResult.hardware.unchanged.length > 0 && (
                            <tr className="bg-slate-50 text-slate-500">
                              <td colSpan={6} className="py-2 px-3 text-center italic">
                                ... và {diffResult.hardware.unchanged.length} chi tiết ngũ kim giống nhau hoàn toàn
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                /* Read-only BomSheet for the old version */
                <BomSheet
                  itemCode={itemCode}
                  productName={productName}
                  productDimensions={productDimensions}
                  assemblyType={selectedHistoryBom.assembly_type}
                  mainWoodType={selectedHistoryBom.woodLines[0]?.wood_type_raw || 'Theo chi tiết'}
                  veneerSpec={selectedHistoryBom.woodLines.find((w) => w.veneer_spec)?.veneer_spec || '—'}
                  bomCode={selectedHistoryBom.code_hint || selectedHistoryBom.name}
                  versionSeq={selectedHistoryBom.version_seq}
                  editCount={selectedHistoryBom.edit_count ?? 0}
                  status={selectedHistoryBom.status}
                  updatedAt={selectedHistoryBom.updated_at}
                  preparedBy={selectedHistoryBom.prepared_by}
                  checkedBy={selectedHistoryBom.checked_by}
                  checkedBy2={selectedHistoryBom.checked_by_2}
                  approvedBy={selectedHistoryBom.approved_by}
                  signedOn={selectedHistoryBom.signed_on}
                  notes={selectedHistoryBom.notes}
                  woodLines={selectedHistoryBom.woodLines}
                  hardwareLines={selectedHistoryBom.hardwareLines}
                  className="shadow-none border-0 p-0"
                />
              )}
            </div>
          )}
        </div>
      </Drawer>
      </div>

      {/* 7. Clone BOM Modal */}
      {showCloneModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm print:hidden">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-md w-full p-6 space-y-4 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-slate-200">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Copy className="h-5 w-5 text-indigo-600" />
                Nhân bản định mức (BOM)
              </h3>
              <button
                type="button"
                onClick={() => setShowCloneModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {cloneError && (
              <div className="p-3 text-xs bg-rose-50 border border-rose-200 rounded text-rose-800">
                {cloneError}
              </div>
            )}

            <div className="space-y-3 text-xs">
              <div>
                <label className="block font-medium text-slate-700 mb-1">Tên BOM mới *</label>
                <input
                  type="text"
                  value={cloneName}
                  onChange={(e) => setCloneName(e.target.value)}
                  className="w-full rounded border border-slate-300 p-2 text-xs focus:border-indigo-500 outline-none"
                  placeholder="Nhập tên BOM mới..."
                />
              </div>

              <div>
                <label className="block font-medium text-slate-700 mb-1">Gắn sản phẩm đích (tùy chọn)</label>
                <select
                  value={cloneProductId}
                  onChange={(e) => setCloneProductId(e.target.value)}
                  className="w-full rounded border border-slate-300 p-2 text-xs focus:border-indigo-500 bg-white outline-none"
                >
                  <option value="">(Giữ nguyên sản phẩm hiện tại)</option>
                  {allProducts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.code} - {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-medium text-slate-700 mb-1">Chế độ lắp ráp</label>
                <div className="flex items-center gap-4 mt-1.5">
                  <label className="flex items-center gap-1.5 cursor-pointer font-medium text-slate-700">
                    <input
                      type="radio"
                      name="cloneAssemblyType"
                      value="assembled"
                      checked={cloneAssemblyType === 'assembled'}
                      onChange={() => setCloneAssemblyType('assembled')}
                    />
                    <span>RÁP CHẾT (FA)</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer font-medium text-slate-700">
                    <input
                      type="radio"
                      name="cloneAssemblyType"
                      value="kd"
                      checked={cloneAssemblyType === 'kd'}
                      onChange={() => setCloneAssemblyType('kd')}
                    />
                    <span>Tháo rời (KD)</span>
                  </label>
                </div>
              </div>

              <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg border border-slate-200">
                Bản sao chép sẽ tạo BOM mới với toàn bộ chi tiết gỗ ({bom.woodLines.length} dòng) và ngũ kim ({bom.hardwareLines.length} dòng), tách riêng một nhóm họ BOM (family) mới ở trạng thái <strong>Bản nháp (inactive)</strong> phiên bản <strong>v1</strong>.
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
              <Button variant="secondary" onClick={() => setShowCloneModal(false)} disabled={cloning}>
                Hủy bỏ
              </Button>
              <Button
                variant="primary"
                onClick={handleExecuteClone}
                disabled={cloning || !cloneName.trim()}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {cloning ? 'Đang tạo bản sao...' : 'Xác nhận nhân bản'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 8. Compare Drawer (with any BOM) */}
      <Drawer
        open={showCompareDrawer}
        onClose={() => {
          setShowCompareDrawer(false)
          setCompareResult(null)
          setSelectedCompareBomId('')
        }}
        title="So sánh định mức BOM — Phân tích sai khác"
        widthClass="w-full max-w-4xl"
      >
        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(100vh-100px)]">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Chọn BOM để so sánh với BOM hiện tại [{bom.name} (v{bom.version_seq})]:
            </label>
            <select
              value={selectedCompareBomId}
              onChange={(e) => void handleSelectCompareBom(e.target.value)}
              className="w-full rounded-lg border border-slate-300 p-2.5 text-xs bg-white focus:border-indigo-500 outline-none"
            >
              <option value="">-- Chọn một BOM khác để so sánh --</option>
              {availableBoms.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} ({b.code_hint || b.id}) · {b.assembly_type === 'assembled' ? 'Ráp chết' : 'Tháo rời'} · v{b.version_seq} [{b.status}]
                </option>
              ))}
            </select>
          </div>

          {compareLoading && (
            <div className="text-center py-12 text-slate-400 text-sm">
              Đang phân tích sai khác giữa 2 BOM...
            </div>
          )}

          {compareError && (
            <div className="p-4 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-800">
              {compareError}
            </div>
          )}

          {compareResult && (
            <div className="space-y-6 animate-in fade-in duration-150">
              {/* Diff summary badges */}
              <div className="flex flex-wrap items-center gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200 text-xs">
                <span className="font-bold text-slate-700">Tổng kết sai khác:</span>
                <span className="px-2.5 py-1 rounded bg-emerald-100 text-emerald-800 font-bold">
                  + {compareResult.wood_lines.added.length + compareResult.hardware_lines.added.length} dòng thêm
                </span>
                <span className="px-2.5 py-1 rounded bg-rose-100 text-rose-800 font-bold">
                  - {compareResult.wood_lines.removed.length + compareResult.hardware_lines.removed.length} dòng xóa
                </span>
                <span className="px-2.5 py-1 rounded bg-amber-100 text-amber-800 font-bold">
                  ~ {compareResult.wood_lines.changed.length + compareResult.hardware_lines.changed.length} dòng sửa
                </span>
              </div>

              {/* Wood lines diff */}
              <div className="space-y-2">
                <h4 className="font-bold text-xs uppercase tracking-wider text-slate-700">
                  1. Chi tiết GỖ ({compareResult.wood_lines.added.length} thêm, {compareResult.wood_lines.removed.length} xóa, {compareResult.wood_lines.changed.length} sửa)
                </h4>

                <div className="border border-slate-200 rounded-lg overflow-hidden bg-white text-xs">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-100 border-b border-slate-200 font-semibold text-slate-700">
                        <th className="py-2 px-3 w-16">Trạng thái</th>
                        <th className="py-2 px-3">Tên chi tiết</th>
                        <th className="py-2 px-3">Loại gỗ</th>
                        <th className="py-2 px-3">Kích thước (D×R×D)</th>
                        <th className="py-2 px-3 w-16 text-center">SL</th>
                        <th className="py-2 px-3">Chi tiết sai khác</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {compareResult.wood_lines.added.map((w, idx) => (
                        <tr key={`cw-add-${idx}`} className="bg-emerald-50/70 text-emerald-950 font-medium">
                          <td className="py-1.5 px-3 text-emerald-700 font-bold">+ Thêm</td>
                          <td className="py-1.5 px-3">{w.part_name}</td>
                          <td className="py-1.5 px-3">{w.wood_type_raw || '—'}</td>
                          <td className="py-1.5 px-3 font-mono">{`${w.dims_finished_t ?? '—'} × ${w.dims_finished_w ?? '—'} × ${w.dims_finished_l ?? '—'}`}</td>
                          <td className="py-1.5 px-3 text-center font-bold">{w.qty_per}</td>
                          <td className="py-1.5 px-3 italic text-emerald-700">Chỉ có ở BOM đích</td>
                        </tr>
                      ))}

                      {compareResult.wood_lines.removed.map((w, idx) => (
                        <tr key={`cw-rem-${idx}`} className="bg-rose-50/70 text-rose-950 line-through">
                          <td className="py-1.5 px-3 text-rose-700 font-bold">- Xóa</td>
                          <td className="py-1.5 px-3">{w.part_name}</td>
                          <td className="py-1.5 px-3">{w.wood_type_raw || '—'}</td>
                          <td className="py-1.5 px-3 font-mono">{`${w.dims_finished_t ?? '—'} × ${w.dims_finished_w ?? '—'} × ${w.dims_finished_l ?? '—'}`}</td>
                          <td className="py-1.5 px-3 text-center">{w.qty_per}</td>
                          <td className="py-1.5 px-3 italic text-rose-700 not-line-through">Bị bớt khỏi BOM đích</td>
                        </tr>
                      ))}

                      {compareResult.wood_lines.changed.map(({ name, base, target, diffs }, idx) => (
                        <tr key={`cw-chg-${idx}`} className="bg-amber-50/70 text-amber-950">
                          <td className="py-1.5 px-3 text-amber-700 font-bold">~ Sửa</td>
                          <td className="py-1.5 px-3 font-medium">{name}</td>
                          <td className="py-1.5 px-3">{target.wood_type_raw || '—'}</td>
                          <td className="py-1.5 px-3 font-mono">
                            <span className="line-through text-slate-400">{`${base.dims_finished_t ?? '—'}×${base.dims_finished_w ?? '—'}×${base.dims_finished_l ?? '—'}`}</span>
                            {' → '}
                            <span className="font-bold text-slate-900">{`${target.dims_finished_t ?? '—'}×${target.dims_finished_w ?? '—'}×${target.dims_finished_l ?? '—'}`}</span>
                          </td>
                          <td className="py-1.5 px-3 text-center font-bold">
                            {base.qty_per !== target.qty_per ? (
                              <>
                                <span className="line-through text-slate-400">{base.qty_per}</span>
                                {' → '}
                                <span>{target.qty_per}</span>
                              </>
                            ) : (
                              target.qty_per
                            )}
                          </td>
                          <td className="py-1.5 px-3 text-amber-800">
                            Thay đổi: <strong>{Object.keys(diffs).join(', ')}</strong>
                          </td>
                        </tr>
                      ))}

                      {compareResult.wood_lines.added.length === 0 &&
                        compareResult.wood_lines.removed.length === 0 &&
                        compareResult.wood_lines.changed.length === 0 && (
                          <tr>
                            <td colSpan={6} className="py-4 text-center text-slate-400 italic">
                              Chi tiết gỗ hoàn toàn giống nhau giữa 2 BOM.
                            </td>
                          </tr>
                        )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Hardware lines diff */}
              <div className="space-y-2">
                <h4 className="font-bold text-xs uppercase tracking-wider text-slate-700">
                  2. Chi tiết NGŨ KIM ({compareResult.hardware_lines.added.length} thêm, {compareResult.hardware_lines.removed.length} xóa, {compareResult.hardware_lines.changed.length} sửa)
                </h4>

                <div className="border border-slate-200 rounded-lg overflow-hidden bg-white text-xs">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-100 border-b border-slate-200 font-semibold text-slate-700">
                        <th className="py-2 px-3 w-16">Trạng thái</th>
                        <th className="py-2 px-3">Tên vật tư</th>
                        <th className="py-2 px-3">Quy cách kỹ thuật</th>
                        <th className="py-2 px-3 w-16 text-center">SL</th>
                        <th className="py-2 px-3 w-20">Đơn vị</th>
                        <th className="py-2 px-3">Chi tiết sai khác</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {compareResult.hardware_lines.added.map((h, idx) => (
                        <tr key={`chw-add-${idx}`} className="bg-emerald-50/70 text-emerald-950 font-medium">
                          <td className="py-1.5 px-3 text-emerald-700 font-bold">+ Thêm</td>
                          <td className="py-1.5 px-3">{h.name_raw}</td>
                          <td className="py-1.5 px-3">{h.spec_raw || '—'}</td>
                          <td className="py-1.5 px-3 text-center font-bold">{h.qty_per}</td>
                          <td className="py-1.5 px-3">{h.uom_hint || '—'}</td>
                          <td className="py-1.5 px-3 italic text-emerald-700">Chỉ có ở BOM đích</td>
                        </tr>
                      ))}

                      {compareResult.hardware_lines.removed.map((h, idx) => (
                        <tr key={`chw-rem-${idx}`} className="bg-rose-50/70 text-rose-950 line-through">
                          <td className="py-1.5 px-3 text-rose-700 font-bold">- Xóa</td>
                          <td className="py-1.5 px-3">{h.name_raw}</td>
                          <td className="py-1.5 px-3">{h.spec_raw || '—'}</td>
                          <td className="py-1.5 px-3 text-center">{h.qty_per}</td>
                          <td className="py-1.5 px-3">{h.uom_hint || '—'}</td>
                          <td className="py-1.5 px-3 italic text-rose-700 not-line-through">Bị bớt khỏi BOM đích</td>
                        </tr>
                      ))}

                      {compareResult.hardware_lines.changed.map(({ name, base, target, diffs }, idx) => (
                        <tr key={`chw-chg-${idx}`} className="bg-amber-50/70 text-amber-950">
                          <td className="py-1.5 px-3 text-amber-700 font-bold">~ Sửa</td>
                          <td className="py-1.5 px-3 font-medium">{name}</td>
                          <td className="py-1.5 px-3">{target.spec_raw || '—'}</td>
                          <td className="py-1.5 px-3 text-center font-bold">
                            {base.qty_per !== target.qty_per ? (
                              <>
                                <span className="line-through text-slate-400">{base.qty_per}</span>
                                {' → '}
                                <span>{target.qty_per}</span>
                              </>
                            ) : (
                              target.qty_per
                            )}
                          </td>
                          <td className="py-1.5 px-3">{target.uom_hint || '—'}</td>
                          <td className="py-1.5 px-3 text-amber-800">
                            Thay đổi: <strong>{Object.keys(diffs).join(', ')}</strong>
                          </td>
                        </tr>
                      ))}

                      {compareResult.hardware_lines.added.length === 0 &&
                        compareResult.hardware_lines.removed.length === 0 &&
                        compareResult.hardware_lines.changed.length === 0 && (
                          <tr>
                            <td colSpan={6} className="py-4 text-center text-slate-400 italic">
                              Chi tiết ngũ kim hoàn toàn giống nhau giữa 2 BOM.
                            </td>
                          </tr>
                        )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </Drawer>

      {/* 8. Paste from Excel / Clipboard Preview Modal */}
      {pasteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm print:hidden">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-2xl w-full p-6 space-y-4 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-slate-200">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-indigo-600" />
                Dán dữ liệu từ Excel / Clipboard
              </h3>
              <button
                type="button"
                onClick={() => setPasteModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div className="flex flex-wrap gap-4 items-center bg-slate-50 p-3 rounded-lg border border-slate-200">
                <div>
                  <span className="font-semibold text-slate-700 mr-2">Bảng đích:</span>
                  <select
                    value={pasteTargetTable}
                    onChange={(e) => setPasteTargetTable(e.target.value as 'wood' | 'hardware')}
                    className="rounded border border-slate-300 bg-white px-2 py-1 text-xs outline-none"
                  >
                    <option value="wood">Bảng 1: Chi tiết Gỗ</option>
                    <option value="hardware">Bảng 2: Ngũ kim / Phụ kiện</option>
                  </select>
                </div>

                <div>
                  <span className="font-semibold text-slate-700 mr-2">Chế độ dán:</span>
                  <select
                    value={pasteMode}
                    onChange={(e) => setPasteMode(e.target.value as 'append' | 'overwrite')}
                    className="rounded border border-slate-300 bg-white px-2 py-1 text-xs outline-none"
                  >
                    <option value="append">Thêm mới vào cuối bảng</option>
                    <option value="overwrite">Ghi đè từ dòng {pasteTargetRowIdx + 1}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-medium text-slate-700 mb-1">
                  Nội dung bảng tính (tự động điền khi bấm Ctrl+V trên bảng, hoặc paste thủ công vào đây):
                </label>
                <textarea
                  rows={4}
                  value={pasteRawText}
                  onChange={(e) => handlePasteChange(e.target.value)}
                  placeholder="Dán các cột từ Excel (ngăn cách bằng phím Tab)..."
                  className="w-full rounded border border-slate-300 p-2 font-mono text-xs focus:border-indigo-500 outline-none"
                />
              </div>

              {parsedPasteRows.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-semibold text-slate-700">
                      Xem trước dữ liệu nhận diện: ({parsedPasteRows.length} dòng,{' '}
                      {parsedPasteRows[0]?.length || 0} cột)
                    </span>
                  </div>
                  <div className="max-h-48 overflow-auto border border-slate-200 rounded-lg bg-slate-50 p-2">
                    <table className="w-full text-left text-xs border-collapse bg-white">
                      <thead>
                        <tr className="bg-slate-100 border-b border-slate-200 text-slate-600">
                          <th className="py-1 px-2 w-8">#</th>
                          {parsedPasteRows[0]?.map((_, colIdx) => (
                            <th key={colIdx} className="py-1 px-2">
                              Cột {colIdx + 1}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {parsedPasteRows.slice(0, 10).map((row, rIdx) => (
                          <tr key={rIdx}>
                            <td className="py-1 px-2 font-mono text-slate-400">{rIdx + 1}</td>
                            {row.map((cell, cIdx) => (
                              <td key={cIdx} className="py-1 px-2 text-slate-700 truncate max-w-[120px]">
                                {cell || '—'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {parsedPasteRows.length > 10 && (
                      <p className="text-xs text-slate-400 text-center mt-2 italic">
                        ... còn {parsedPasteRows.length - 10} dòng nữa
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
              <Button
                variant="secondary"
                onClick={() => setPasteModalOpen(false)}
                className="text-xs"
              >
                Hủy bỏ
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmPaste}
                disabled={parsedPasteRows.length === 0}
                className="text-xs bg-indigo-600 hover:bg-indigo-700"
              >
                Xác nhận dán ({parsedPasteRows.length} dòng)
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

